import React, { Component, createRef } from 'react';
import { connect } from 'react-redux';
import { CommonLayout } from './layout';
import { PdfToolHeader } from '../pdfToolHeader';
import UploadFileContainer, { uploadFileClick } from '../upload/uploadFile';
import Error from '../error';
import Reload from '../reload';
import BlogTool from '../blogTool';
import { onSetToolAction, onSetUploadWindowTrigger, onClear } from '../../reducers/toolLayoutReducer';
import { onLeaveTool, onSetRedirectionInfo, onRemoveAllFiles } from '../../reducers/uploadFileReducer';
import { Metadata } from '../../metadata';
import tools, { globalPageId } from '../../../constants/tools';
import HowToUse from '../howToUse';
import { DidYouKnow } from '../DidYouKnow';
import EmbedButton from '../embedButton';
import { steps, redirectionSteps } from '../../../constants/steps';
import { withRouter } from 'react-router';
import loadable from '@loadable/component';
import ToolService from '../../../common/services/toolsService';
import { AsyncSVG } from '../AsyncSVG';
import OfferAfterProcessed from '../offerAfterProcessed';
import { FileAreSafe } from '../fileSafe'
import { Merchandizer } from '../GdPictureMerch';
import i10n from '../../i10n';
import { LLMSection } from '../LLMSection';

const toolLoader = loadable(() => import("../../../pages/toolPages"))
const ToolWidget = loadable(() => import('./widgetLayout'));
const Download = loadable(() => import('../download/download'));

export class ToolLayoutView extends Component {
    constructor(props) {
        super(props);
        this.state = {
            fileDeleted: false,
            uploadFileInstanceKey: "0",
            toolCustumization: {},
            componentMapLoaded: false
        }
    }

    componentDidMount() {
        if (!window.embedded && this.props.redirectionStep === redirectionSteps.inRedirection
            || (this.props.redirectionStep === redirectionSteps.beforeRedirection && this.props.tool.id !== this.props.redirectToWidget)) {
            this.props.setRedirectionInfo(redirectionSteps.noRedirection, null, null, null);
            this.props.onLeaveTool();
        }

        this.initTool();
    }

    componentWillUnmount() {
        ToolService.cancelRequest();
        this.props.onLeaveTool();
    }

    componentDidUpdate(prevProps) {
        if (this.props.tool !== prevProps.tool) {
            this.props.clear();
            this.loadComponent().then(() => this.setState({ toolCustumization: {} })).then(this.initTool);
        }
        if (this.props.step === steps.action && this.state.componentMapLoaded) {
            Download.preload();
        }
        if (prevProps.proceseedContentAvailable.length !== this.props.proceseedContentAvailable.length) {
            const isCurrentContextIdRemoved =
                prevProps.proceseedContentAvailable.some(pc => pc.id === this.props.processedContextId) &&
                !this.props.proceseedContentAvailable.some(pc => pc.id === this.props.processedContextId);

            if (isCurrentContextIdRemoved) {
                this.onReload();
            }
        }
    }

    initTool = () => {
        if (window.embedded) {
            ToolWidget.preload();
            let componentLoad = this.loadComponent();
            if (this.props.tool.uploadFileTypes === null) {
                this.props.setStep(steps.loadingTool);
                componentLoad.then(() => this.props.setStep(steps.action))
            }
        } else if (this.props.tool.uploadFileTypes === null) {
            this.props.setStep(steps.action);
            this.loadComponent();
        }
    }

    onUpload = async (res) => {
        if (!this.props.tool.shouldNotGoToActionStepAfterFileUpload) {
            this.props.setStep(steps.action);
        }
        this.setState({ fileDeleted: false });
    }

    uploadFileClick = () => {
        uploadFileClick();
        this.props.setUploadWindowTrigger(true);
    }

    onFileDeleted = () => this.setState({ fileDeleted: true });

    GetReloadBehaviour = () => {
        if (this.state.toolCustumization && this.state.toolCustumization.onReload && [steps.action, steps.progress].includes(this.props.step)) {
            return { onReloadFn: this.state.toolCustumization.onReload };
        }
        if (this.props.tool === tools.scanningToPdf) {
            return { redirectToStep: steps.action, keepFileContext: true };
        } else if (this.props.step === steps.action && this.props.uploadMore) {
            return { redirectToStep: steps.upload, keepFileContext: false };
        } else {
            return { redirectToStep: steps.upload, keepFileContext: false }
        }
    }

    childComponent = createRef()

    loadComponent = () => {
        return toolLoader.load().then(module => {
            let tool = module.toolComponentLink.find(tool => tool.tool.id === this.props.tool.id);
            if (!tool) {
                throw "";
            }
            this.childComponent = React.createElement(tool.component, { toolInfo: tool.tool, setToolCustumization: this.getToolCustumization });
            this.setState({ componentMapLoaded: true });
        }).catch((e) => {
            if (process.env.NODE_ENV !== 'production') {
                console.log(e);
                throw "This widget is not configured in src/pages/toolPages/index.jsx";
            }
        });
    }

    getToolCustumization = (data) =>
        this.setState({
            toolCustumization: data
        });

    onReload = () => {
        this.setState({ fileDeleted: false });
        const reloadBehaviour = this.GetReloadBehaviour();
        if (reloadBehaviour.onReloadFn) {
            reloadBehaviour.onReloadFn();
            return;
        }
        if (reloadBehaviour && typeof reloadBehaviour.keepFileContext !== "undefined" && !reloadBehaviour.keepFileContext)
            this.props.removeAllFiles();

        if (reloadBehaviour && reloadBehaviour.redirectToStep) {
            this.props.clear({ step: reloadBehaviour.redirectToStep });
        }
        else {
            this.props.clear();
        }
    }

    getToolUploadTitle = () => {
        const acceptedFiles = this.props.tool.uploadFileTypes;
        if (acceptedFiles === null) {
            return null;
        }

        if (typeof acceptedFiles.label === 'undefined' && this.props.tool.uploadMultipleFiles === false) { //single, no custom file type label
            return i10n["_VIEW_UPLOAD_FILE_DROP"];
        } else if (typeof acceptedFiles.label === 'undefined' && this.props.tool.uploadMultipleFiles === true) { //multiple, no custom file type label
            return i10n["_VIEW_UPLOAD_FILE_DROP_MULT"];
        } else if (typeof acceptedFiles.label !== 'undefined' && this.props.tool.uploadMultipleFiles === false) { //single, with custom file type label
            return i10n["_VIEW_UPLOAD_FILE_DROP_PLACEHOLDER"].replace("#fileType", acceptedFiles.label);
        } else if (typeof acceptedFiles === 'object' && this.props.tool.uploadMultipleFiles === true) { //single, with custom file type label
            return i10n["_VIEW_UPLOAD_FILE_DROP_MULT_PLACEHOLDER"].replace("#fileType", acceptedFiles.label);
        }

        return i10n["_VIEW_UPLOAD_FILE_DROP"];
    }

    render() {
        var fileTypes = this.props.tool.uploadFileTypes;
        let shouldDisplayFileInfo = this.props.tool !== tools.scanningToPdf && (this.props.step == steps.success || (this.props.step == steps.action && this.props.showFileDetail)) && !this.state.fileDeleted;
        var fileSize = this.props.toolFiles && this.props.toolFiles[0]?.fileSize ? this.props.toolFiles[0].fileSize : 0;

        return (
            <div className={`${this.props.tool.fillClassName} ${this.props.tool.route}`} key={this.props.tool.id} >
                {
                    window.embedded &&
                    <ToolWidget
                        inititalToolFiles={this.props.inititalToolFiles}
                        toolFiles={this.props.toolFiles}
                        toolInfo={this.props.tool}
                        image={this.props.image}
                        title={this.props.title}
                        subtitle={this.props.subtitle}
                        pageId={this.props.pageId}
                        howToUseLocalizationKeyPart={this.props.howToUseLocalizationKeyPart}
                        uploadSuccess={this.props.uploadSuccess}
                        uploadTitle={this.getToolUploadTitle()}
                        uploadButton={this.props.uploadButton}
                        uploadSubtitle={this.props.uploadSubtitle}
                        showFileDetail={this.props.showFileDetail}
                        shouldNotGoToActionStepAfterFileUpload={this.props.shouldNotGoToActionStepAfterFileUpload}
                        outputFileSize={fileSize}
                        uploadMore={this.state.toolCustumization.uploadMore}
                        uploadAvoidPreload={this.props.tool.uploadAvoidPreload}
                        meta={this.props.meta}
                        toolAcceptsPasswordProtectedFiles={this.props.tool.toolAcceptsPasswordProtectedFiles}
                        thumbnailConfig={this.props.tool.thumbnail}
                        onReload={this.onReload}
                        step={this.props.step}>
                        {
                            this.childComponent
                        }
                    </ToolWidget>
                    || !window.embedded &&
                    <CommonLayout pageId={`${this.props.tool.id},${globalPageId}`} enablePreviousFileDnd={true}>
                        <Metadata tool={this.props.tool} />
                        <PdfToolHeader tool={this.props.tool} />
                        <div className="row ave-row-medium">
                            <div className="col">
                                <div className="tools-box h-100" id="dragAndDropArea">
                                    <div className="row h-100 align-items-center justify-content-center">
                                        {
                                            (this.props.step === steps.upload || (this.props.step === steps.action && this.state.toolCustumization.uploadMore)) &&
                                            <UploadFileContainer
                                                key={this.state.uploadFileInstanceKey}
                                                onUpload={this.onUpload}
                                                uploadTitle={this.getToolUploadTitle()}
                                                subtitle={this.props.uploadSubtitle}
                                                uploadButton={this.props.uploadButton}
                                                fileTypes={fileTypes}
                                                icon={this.props.tool.uploadFileIcon}
                                                multipleFiles={this.props.tool.uploadMultipleFiles}
                                                buttonBackground={this.props.tool.fillClassName}
                                                toolInfo={this.props.tool}
                                                uploadMore={this.state.toolCustumization.uploadMore}
                                                uploadAvoidPreload={this.props.tool.uploadAvoidPreload}
                                                toolAcceptsPasswordProtectedFiles={this.props.tool.toolAcceptsPasswordProtectedFiles}
                                                thumbnailConfig={this.props.tool.thumbnail}
                                                additionalUploadOption={this.props.additionalUploadOption}
                                                defaultUploadOpen={this.props.defaultUploadOpen}
                                                onClick={this.loadComponent}
                                            />
                                        }
                                        {
                                            this.props.step === steps.action && this.state.componentMapLoaded === false &&
                                            <div className="loading-tool">
                                                <AsyncSVG className="loading-icon rounded-icon" src="/images/black-avepdf-svg-loading.svg" />
                                            </div>
                                        }
                                        {
                                            (this.props.step === steps.progress || this.props.step === steps.action || this.props.step === steps.loadingTool) && this.state.componentMapLoaded &&
                                            this.childComponent
                                        }
                                        {
                                            (this.state.toolCustumization.thumbnailsOnUploadMore && this.props.step === steps.upload && this.props.toolFiles.length > 0) &&
                                            <ul className="layout-thumbnails text-center w-100">
                                                {
                                                    this.state.toolCustumization.thumbnailsOnUploadMore
                                                }
                                            </ul>
                                        }
                                        {
                                            this.props.step === steps.success &&
                                            <Download
                                                successTitle={this.props.successMessage}
                                                successSubtitle={this.props.successSubMessage}
                                                outputFileSize={this.props.outputFileSize}
                                                iconFillClassName={this.props.tool.fillClassName}
                                                outputFileName={this.props.outputFileName}
                                                offerAfterProcessed={this.props.tool.offerAfterProcessed}
                                                notOfferAfterProcessed={this.props.tool.notOfferAfterProcessed}
                                                onFileDeleted={this.onFileDeleted}
                                            />
                                        }
                                        {
                                            this.props.step === steps.error &&
                                            <Error
                                                subtitle={this.props.errorMessage}
                                                title={this.props.errorTitle}
                                                isWarning={this.props.isWarning}
                                                backButtonColor={this.props.tool.fillClassName}
                                                redirectionStep={this.props.redirectionStep}
                                                onReload={this.onReload}
                                            />
                                        }
                                    </div>
                                    {
                                        this.props.step != steps.upload &&
                                        this.props.step != steps.progress &&
                                        this.props.step != steps.error &&
                                        this.props.step != steps.loadingTool &&
                                        this.props.step != this.GetReloadBehaviour().redirectToStep &&
                                        <Reload onReload={this.onReload} />
                                    }
                                    <EmbedButton
                                        route={this.props.tool.route}
                                        fillClassName={this.props.tool.fillClassName}
                                    />
                                </div>
                            </div>
                        </div>
                        <Merchandizer tool={this.props.tool} />
                        {
                            !this.state.fileDeleted && this.props.step === steps.success &&
                            <OfferAfterProcessed
                                offerTools={this.props.tool.offerAfterProcessed}
                                notOfferTools={this.props.tool.notOfferAfterProcessed} />
                        }
                        {
                            this.props.step === steps.upload &&
                            <FileAreSafe />
                        }
                        {
                            this.props.step != steps.progress &&
                            <HowToUse
                                keyPart={this.props.tool.howToUseLocalizationKeyPart}
                                icon={this.props.tool.ratingIcon}
                                iconFillClassName={this.props.tool.fillClassName} />
                        }

                        { /* we need to pass the list of articles coming from the tool.. */}
                        <BlogTool blogFromTools={this.props.tool.blogArticles} />

                        {
                            this.props.step != steps.progress && this.props.tool.didYouKnow &&
                            <DidYouKnow columns={this.props.tool.didYouKnow} />
                        }
                        <LLMSection tool={this.props.tool} />
                    </CommonLayout>
                }
            </div>
        );
    }
};
const mapState = ({ tool, files }, ownProps) => {
    return {
        step: ownProps.step ? ownProps.step : tool.step,
        errorMessage: tool.errorMessage,
        errorTitle: tool.errorTitle,
        isWarning: tool.isWarning,
        successMessage: tool.successMessage,
        successSubMessage: tool.successSubMessage,
        outputFileSize: files.outputFileSize,
        outputFileName: files.outputFileName,
        redirectionStep: files.redirectionStep,
        redirectToWidget: files.redirectToWidget,
        mainTool: files.mainTool,
        files: files.files,
        language: tool.lang,
        waitingFiles: files.inQueueFiles,
        pendingRemoteFiles: files.pendingRemoteFiles,
        proceseedContentAvailable: files.processedContext,
        processedContextId: files.processedContextId
    };
}
const mapDispatch = (dispatch) => {
    return {
        setStep(step) {
            dispatch(onSetToolAction(step));
        },
        setUploadWindowTrigger(visible) {
            dispatch(onSetUploadWindowTrigger(visible));
        },
        onLeaveTool() {
            dispatch(onLeaveTool());
        },
        setRedirectionInfo(step) {
            dispatch(onSetRedirectionInfo(step));
        },
        clear(behaviour) {
            dispatch(onClear(behaviour));
        },
        removeAllFiles() {
            dispatch(onRemoveAllFiles());
        }
    }
}
const ToolLayout = withRouter(connect(mapState, mapDispatch)(ToolLayoutView));
export default ToolLayout;